import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, CalendarPlus, Check, X, FileSignature, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { usePipelineMeta, StageBadge, ChecklistPill } from "@/lib/pipeline";
import { ReqBadge } from "@/pages/Requirements";
import { SEA_EMPTY, SeaFields, toSeaPayload, validateSea } from "@/components/SeaForm";

function AssignDialog({ cand, reqId, onClose, onSaved }) {
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Set the interview date"); return; }
    try { await api.post(`/pipeline/requirements/${reqId}/assign`, { candidate_id: cand.id, interview_date: date, notes }); toast.success("Candidate assigned, interview scheduled"); onClose(); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={!!cand} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assign {cand?.full_name}</DialogTitle><DialogDescription>Moves the candidate to Interview Scheduled.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="as-date">Interview date</Label><Input id="as-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="assign-interview-date-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="as-notes">Notes</Label><Input id="as-notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="assign-notes-input" /></div>
          <DialogFooter><Button type="submit" className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="assign-submit-btn">Assign</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SeaDialog({ target, req, onClose, onSaved }) {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(SEA_EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!target) return;
    const c = target.candidate;
    setForm({ ...SEA_EMPTY, seafarer_name: c.full_name, rank: req.rank || c.rank, cdc_number: c.cdc_number || "", passport_number: c.passport_number || "",
      joining_date: req.joining_date || "", joining_port: req.joining_port || "", seafarer_address: { ...SEA_EMPTY.seafarer_address, ...(c.address || {}) } });
    api.get(`/vessels/${req.vessel_id}/sea-form`).then(({ data }) => setInfo(data)).catch((err) => toast.error(formatApiError(err)));
  }, [target, req]);
  const submit = async (e) => {
    e.preventDefault();
    const err = validateSea(form);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/vessels/${req.vessel_id}/sea-contracts`, toSeaPayload(form));
      try {
        await api.post(`/pipeline/assignments/${target.assignment_id}/link-sea`, { sea_contract_id: data.id });
      } catch (linkErr) {
        await api.delete(`/sea-contracts/${data.id}`).catch(() => {});
        throw linkErr;
      }
      toast.success("SEA generated — stage moved to SEA Generated");
      onClose(); onSaved();
    } catch (er) { toast.error(formatApiError(er)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pipeline-sea-dialog">
        <DialogHeader><DialogTitle>Generate SEA</DialogTitle><DialogDescription>{info?.template ? `Template: ${info.template.owner_name}` : "No SEA template for this vessel's owner — ask Super Admin to upload one."}</DialogDescription></DialogHeader>
        {info && (
          <form onSubmit={submit} className="space-y-4">
            <SeaFields form={form} setForm={setForm} ranks={info.ranks} vesselName={info.vessel_name} ownerName={info.owner_name} />
            <DialogFooter><Button type="submit" disabled={saving || !info.template} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="pipeline-sea-submit-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate SEA"}</Button></DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AssignedRow({ a, canManage, canSea, seaEnabled, onAct, onSea }) {
  const g7 = (a.candidate?.groups || []).find((g) => g.id === 7);
  const g7Done = g7 && g7.done === g7.total;
  const g10 = (a.candidate?.groups || []).find((g) => g.id === 10);
  const g10Done = g10 && g10.done === g10.total;
  const result = (r) => { const notes = window.prompt(`Remarks for interview ${r}`); if (notes !== null) onAct("interview", { result: r, notes }); };
  const c = a.candidate || {};
  const s = a.current_stage;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100" data-testid={`assigned-row-${a.assignment_id}`}>
      <div className="min-w-0">
        <Link to={`/candidates/${c.id}`} className="font-semibold text-slate-800 hover:text-cyan-700">{c.full_name}</Link>
        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">Interview {a.interview_date || "—"} <StageBadge stage={s} testid={`assigned-stage-${a.assignment_id}`} /></div>
      </div>
      {(canManage || canSea) && (
        <div className="flex flex-wrap gap-2">
          {canManage && s === "Interview Scheduled" && <>
            <Button size="sm" disabled={!g7Done} title={g7Done ? "" : "Tick all 6 Superintendent Assessment items first"} className="bg-emerald-700 hover:bg-emerald-800" onClick={() => result("pass")} data-testid={`interview-pass-btn-${a.assignment_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Pass</Button>
            <Button size="sm" variant="outline" disabled={!g7Done} className="text-rose-600" onClick={() => result("fail")} data-testid={`interview-fail-btn-${a.assignment_id}`}><X className="w-3.5 h-3.5 mr-1" /> Fail</Button>
          </>}
          {s === "Interview Passed" && canSea && <Button size="sm" disabled={!seaEnabled} title={seaEnabled ? "" : "SEA generation not enabled for you — ask Super Admin"} className="bg-[#0b192c] hover:bg-[#0f2544]" onClick={onSea} data-testid={`generate-sea-btn-${a.assignment_id}`}><FileSignature className="w-3.5 h-3.5 mr-1" /> Generate SEA</Button>}
          {canManage && s === "Interview Scheduled" && !g7Done && <span className="text-xs text-amber-700" data-testid={`g7-pending-${a.assignment_id}`}>Superintendent Assessment: {g7?.done || 0}/6</span>}
          {canManage && s === "SEA Generated" && <Button size="sm" variant="outline" onClick={() => onAct("advance", { stage: "Offer Sent" })} data-testid={`offer-sent-btn-${a.assignment_id}`}>Offer Sent <ChevronRight className="w-3.5 h-3.5 ml-1" /></Button>}
          {canManage && s === "Offer Sent" && <Button size="sm" variant="outline" disabled={!g10Done} title={g10Done ? "" : "Management final sign-off pending"} onClick={() => onAct("advance", { stage: "Joined" })} data-testid={`joined-btn-${a.assignment_id}`}>Joined <ChevronRight className="w-3.5 h-3.5 ml-1" /></Button>}
        </div>
      )}
    </div>
  );
}

export default function RequirementDetailPage() {
  const { id } = useParams();
  const meta = usePipelineMeta();
  const { hasPermission } = useAuth();
  const [r, setR] = useState(null);
  const [assign, setAssign] = useState(null);
  const [sea, setSea] = useState(null);
  const load = () => api.get(`/pipeline/requirements/${id}`).then(({ data }) => setR(data)).catch((err) => { toast.error(formatApiError(err)); setR(false); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (r === null || !meta) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline" /></div>;
  if (r === false) return <div className="py-20 text-center text-slate-500">Requirement not found.</div>;
  const canManage = meta.caps.can_manage;
  const act = async (aid, kind, body) => { try { await api.post(`/pipeline/assignments/${aid}/${kind}`, body); toast.success("Updated"); load(); } catch (err) { toast.error(formatApiError(err)); } };
  const setStatus = async (s) => { try { await api.patch(`/pipeline/requirements/${r.id}`, { requirement_status: s }); load(); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="space-y-6" data-testid="requirement-detail-page">
      <Link to="/requirements" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="w-4 h-4" /> Crew Requirements</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">{r.vessel_name}</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }} data-testid="requirement-detail-rank">{r.rank} × {r.required_count}</h1>
          <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-3">Joining {r.joining_date || "—"} at {r.joining_port || "—"} <ReqBadge s={r.requirement_status} testid="requirement-detail-status" /></div>
        </div>
        {canManage && <div className="flex gap-1.5">{["open", "filled", "cancelled"].filter((s) => s !== r.requirement_status).map((s) => <Button key={s} size="sm" variant="outline" onClick={() => setStatus(s)} data-testid={`req-set-${s}-btn`}>Mark {s}</Button>)}</div>}
      </div>
      <div className="bg-white rounded-xl border border-slate-200" data-testid="assigned-list">
        <div className="px-4 py-3 text-sm font-semibold text-slate-800">Assigned candidates ({r.assigned.length})</div>
        {r.assigned.map((a) => <AssignedRow key={a.assignment_id} a={a} canManage={canManage} canSea={canManage || meta.caps.docs} seaEnabled={meta.caps.sea_generate} onAct={(k, b) => act(a.assignment_id, k, b)} onSea={() => setSea(a)} />)}
        {!r.assigned.length && <div className="px-4 pb-4 text-sm text-slate-400">No one assigned yet.</div>}
      </div>
      <div className="bg-white rounded-xl border border-slate-200" data-testid="shortlisted-list">
        <div className="px-4 py-3 text-sm font-semibold text-slate-800">Shortlisted {r.rank} candidates ({r.shortlisted.length})</div>
        {r.shortlisted.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100" data-testid={`shortlisted-row-${c.id}`}>
            <div><Link to={`/candidates/${c.id}`} className="font-semibold text-slate-800 hover:text-cyan-700">{c.full_name}</Link> <span className="text-xs font-mono text-slate-400 ml-2">{c.cdc_number}</span> <ChecklistPill done={c.checklist_done} total={c.checklist_total} /></div>
            {canManage && <Button size="sm" onClick={() => setAssign(c)} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid={`assign-btn-${c.id}`}><CalendarPlus className="w-3.5 h-3.5 mr-1" /> Assign & schedule interview</Button>}
          </div>
        ))}
        {!r.shortlisted.length && <div className="px-4 pb-4 text-sm text-slate-400">No shortlisted candidates with this rank.</div>}
      </div>
      <AssignDialog cand={assign} reqId={r.id} onClose={() => setAssign(null)} onSaved={load} />
      <SeaDialog target={sea} req={r} onClose={() => setSea(null)} onSaved={load} />
    </div>
  );
}
